import * as SwitchPrimitives from '@radix-ui/react-switch'
import * as React from 'react'

import { cn } from './utils'

type SwitchSize = 'sm' | 'md' | 'lg'

const sizeClasses: Record<
  SwitchSize,
  {
    root: string
    thumb: string
    translate: string
  }
> = {
  sm: {
    root: 'h-4 w-7',
    thumb: 'h-3 w-3',
    translate:
      'data-[state=checked]:translate-x-3.5 data-[state=unchecked]:translate-x-0',
  },
  md: {
    root: 'h-6 w-11',
    thumb: 'h-5 w-5',
    translate:
      'data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
  },
  lg: {
    root: 'h-8 w-16',
    thumb: 'h-7 w-7',
    translate:
      'data-[state=checked]:translate-x-9 data-[state=unchecked]:translate-x-0',
  },
}

type SwitchProps = React.ComponentPropsWithoutRef<
  typeof SwitchPrimitives.Root
> & {
  thumbIconChecked?: React.ReactNode
  thumbIconUnchecked?: React.ReactNode
  size?: SwitchSize
}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  SwitchProps
>(
  (
    { className, thumbIconChecked, thumbIconUnchecked, size = 'md', ...props },
    ref
  ) => {
    const { root, thumb, translate } = sizeClasses[size]
    return (
      <SwitchPrimitives.Root
        className={cn(
          'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-input',
          root,
          className
        )}
        {...props}
        ref={ref}
        checked={props.checked}
        onCheckedChange={props.onCheckedChange}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            'pointer-events-none flex items-center justify-center rounded-full bg-background shadow-lg ring-0 transition-transform',
            thumb,
            translate
          )}
        >
          {props.checked && thumbIconChecked}
          {!props.checked && thumbIconUnchecked}
        </SwitchPrimitives.Thumb>
      </SwitchPrimitives.Root>
    )
  }
)
Switch.displayName = SwitchPrimitives.Root.displayName

export { Switch }
